import React, { useEffect, useRef, useState } from "react";
import "./LinkModal.scss";

const LinkModal = ({ isOpen, onClose, onSave, initialUrl = "", initialText = "", isEdit = false }) => {
    const [url, setUrl] = useState(initialUrl);
    const [text, setText] = useState(initialText);
    const [error, setError] = useState("");
    const urlInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setUrl(initialUrl);
            setText(initialText);
            setError("");
            setTimeout(() => {
                if (urlInputRef.current) {
                    urlInputRef.current.focus();
                }
            }, 100);
        }
    }, [isOpen, initialUrl, initialText]);

    const validateUrl = (url) => {
        if (!url.trim()) return false;
        const urlPattern = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
        return urlPattern.test(url.trim());
    };

    const handleSave = () => {
        const trimmedUrl = url.trim();
        const trimmedText = text.trim();

        if (!trimmedUrl) {
            setError("URL is required");
            return;
        }

        if (!validateUrl(trimmedUrl)) {
            setError("Please enter a valid URL");
            return;
        }

        let finalUrl = trimmedUrl;
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
            finalUrl = 'https://' + trimmedUrl;
        }

        onSave({
            url: finalUrl,
            text: trimmedText || finalUrl
        });

        onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    const handleRemoveLink = () => {
        onSave({ url: null, text: text });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="link-modal-overlay" onClick={onClose}>
            <div className="link-modal" onClick={(e) => e.stopPropagation()}>
                <div className="link-modal-header">
                    <h3>{isEdit ? 'Edit Link' : 'Add Link'}</h3>
                    <button className="close-btn" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className="link-modal-body">
                    <div className="form-group">
                        <label htmlFor="link-url">URL</label>
                        <input
                            ref={urlInputRef}
                            id="link-url"
                            type="text"
                            value={url}
                            onChange={(e) => {
                                setUrl(e.target.value);
                                setError("");
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder="https://example.com"
                            className={error ? 'error' : ''}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="link-text">Display Text (optional)</label>
                        <input
                            id="link-text"
                            type="text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Link text"
                        />
                        <small className="form-help">If empty, the URL will be used as display text</small>
                    </div>

                    {error && <div className="error-message">{error}</div>}
                </div>

                <div className="link-modal-footer">
                    {isEdit && (
                        <button
                            type="button"
                            className="remove-link-btn"
                            onClick={handleRemoveLink}
                        >
                            Remove Link
                        </button>
                    )}
                    <div className="modal-actions">
                        <button type="button" className="cancel-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" className="save-btn" onClick={handleSave}>
                            {isEdit ? 'Update' : 'Add'} Link
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LinkModal;
